/**
 * Stripe Connect onboarding page (dashboard-organizer view).
 *
 * This page is *the* primary entry point for dashboard organizers, who do
 * NOT have access to the Payment Processors tab. Admins also see this page
 * (with a slightly broader view), but ops users still manage payment
 * processors fully through Payment Processors.
 *
 * What dashboard organizers can do here:
 *   - Create a new Stripe Connect account ("New Stripe Connect Account").
 *   - See their processors and current Stripe Connect status.
 *   - Continue setup / open the Stripe Express dashboard.
 *   - Add or edit the Terminal Location required for card readers.
 *   - Get a yellow warning flag when a connected account has no Terminal
 *     Location yet.
 *
 * What they CANNOT do here:
 *   - Edit the processor's name, fee, processing rate, or any of the other
 *     fields owned by ops on the Payment Processors page.
 *   - Unlink an account (admin-only — non-admins shouldn't accidentally
 *     orphan their connected account).
 *   - Relink a previous account (admin-only).
 */

import { gql } from "@apollo/client";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  LoaderFunctionArgs,
  useLoaderData,
  useRevalidator,
  useSearchParams,
} from "react-router-dom";
import { toast } from "react-hot-toast";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  ICellRendererParams,
} from "ag-grid-community";
import moment from "moment/moment";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import { Input } from "../../components/inputs/Input";
import {
  PROCESSOR_SEARCH_PLACEHOLDER,
  matchesProcessorSearch,
} from "../payment_processor_config/search";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { organizationStore, useOrganizationStore } from "../../store/organization";
// Role comes from the access token, not sessionStorage — see
// providers/AuthProvider.getDashboardRole. Reading sessionStorage here is what
// silently showed an admin a non-admin's handful of rows.
import { isAdminUser } from "../../providers/AuthProvider";
import { useEventStore } from "../../store/event";
import { userStore } from "../../store/user";
import {
  // completeOAuthLink* stay imported: OAuth link CREATION is retired (the
  // backend answers 410), but authorizations already in flight when that
  // shipped still redirect back here with ?code=&state= and must complete.
  completeOAuthLink,
  completeOAuthLinkForNew,
  createLoginLink,
  createOnboardingLink,
  deauthorizeAccount,
  disconnectAccount,
  refreshAccountStatus,
  refreshAllAccountStatuses,
  relinkPreviousAccount,
} from "./api";
import { StripeProcessorRow } from "./types";
import { NewStripeConnectAccountModal } from "./NewStripeConnectAccountModal";
import { TerminalLocationModal } from "./TerminalLocationModal";
import { StripeAccountDetailsPanel } from "./StripeAccountDetailsPanel";
import { DeletePaymentProcessor } from "../payment_processor_config/mutations";
import { GET_RECORDS_USING_PROCESSOR } from "../payment_processor_config/queries";
import ConfirmDialog from "../../components/ConfirmDialog";

const STRIPE_PROCESSORS_QUERY = gql`
  query stripe_processors($where: payment_processor_config_bool_exp) {
    payment_processor_config(
      where: $where
      order_by: { payment_processor: asc }
    ) {
      id
      payment_processor
      is_active
      organization_id
      created_at
      processor
      config
      organization {
        id
        name
      }
    }
  }
`;

const client = createApolloClient();


export const stripeConnectLoader = async ({}: LoaderFunctionArgs) => {
  const rawOrgId = organizationStore.getState().organizationId;
  const organizationId = parseInt(
    rawOrgId !== null ? rawOrgId.toString() : "",
    10
  );
  const user = userStore.getState().user;
  const currentUserId = user?.id;
  const admin = isAdminUser();

  const where: any = { processor: { _eq: "stripe" } };
  if (!admin) {
    if (organizationId && organizationId !== 0) {
      where.organization_id = { _eq: organizationId };
    }
    // Visibility for non-admins. `last_updated_by` alone was WRONG as an
    // ownership filter: it holds whoever last SAVED the row, so the moment
    // ops created a row on a vendor's behalf (or just tweaked its fee), the
    // row disappeared from that vendor's Stripe Connect page entirely.
    // Vendor-scoped users (e.g. "vendor — reporting only") now see every row
    // stamped with one of THEIR vendor ids (config.vendor_id, written by the
    // Connect flow); last_updated_by stays as a fallback so rows that predate
    // vendor stamping — and dashboard-organizer behavior — are unchanged.
    const vendorIds: number[] = (user as any)?.vendors || [];
    const visibility: any[] = [];
    for (const v of vendorIds) {
      visibility.push({ config: { _contains: { vendor_id: v } } });
      // Older rows occasionally stored the id as a string — match both.
      visibility.push({ config: { _contains: { vendor_id: String(v) } } });
    }
    if (currentUserId != null) {
      visibility.push({ last_updated_by: { _eq: currentUserId } });
    }
    if (visibility.length) {
      where._or = visibility;
    }
  }

  const { data } = await client.query({
    query: STRIPE_PROCESSORS_QUERY,
    variables: { where },
  });
  return data.payment_processor_config as StripeProcessorRow[];
};

// --- pure helpers --------------------------------------------------------

function isFullyOnboarded(p: StripeProcessorRow): boolean {
  const c = p.config || {};
  return (
    !!c.stripe_charges_enabled &&
    !!c.stripe_payouts_enabled &&
    !!c.stripe_details_submitted
  );
}
function hasConnectedAccount(p: StripeProcessorRow): boolean {
  return !!p.config?.connected_account_id;
}
function hasPreviousAccount(p: StripeProcessorRow): boolean {
  return !!p.config?.previous_connected_account_id;
}
function hasTerminalLocation(p: StripeProcessorRow): boolean {
  return !!p.config?.platform_account_location_id;
}
function statusLabel(p: StripeProcessorRow): string {
  if (!hasConnectedAccount(p)) return "Not Connected";
  if (isFullyOnboarded(p)) return "Active";
  if (p.config?.stripe_details_submitted) return "Pending Verification";
  return "Onboarding Incomplete";
}
function statusDotColor(p: StripeProcessorRow): string {
  if (!hasConnectedAccount(p)) return "bg-gray-300";
  if (isFullyOnboarded(p)) return "bg-green-500";
  return "bg-yellow-500";
}
// An orphan is an abandoned onboarding: no connected account AND no stashed
// previous account (an unlinked-but-relinkable row is NOT an orphan). Only
// these rows are ever offered for deletion here.
function isOrphanRow(p: StripeProcessorRow): boolean {
  return !hasConnectedAccount(p) && !hasPreviousAccount(p);
}

// --- orphan deletion (admin-only) ----------------------------------------

// Web-checkout assignment lives inside events.web_checkout_configurations
// (jsonb) and is not covered by GET_RECORDS_USING_PROCESSOR. The id was
// historically stored as a string; check both representations.
const WEB_CHECKOUT_USAGE = gql`
  query webCheckoutUsage($idStr: jsonb!, $idNum: jsonb!) {
    str: events_aggregate(
      where: { web_checkout_configurations: { _contains: $idStr } }
    ) {
      aggregate {
        count
      }
    }
    num: events_aggregate(
      where: { web_checkout_configurations: { _contains: $idNum } }
    ) {
      aggregate {
        count
      }
    }
  }
`;

/**
 * Live usage re-check immediately before deletion. FAILS CLOSED: any query
 * error reports the row as in use, so a flaky network can never cause a
 * deletion that unhooks an event or location.
 */
async function processorUsageSummary(
  processorId: number
): Promise<{ inUse: boolean; summary: string }> {
  try {
    const { data } = await client.query({
      query: GET_RECORDS_USING_PROCESSOR,
      variables: { processorId },
      fetchPolicy: "network-only",
    });
    const counts: Array<[string, number]> = [
      ["locations", data?.locations?.length ?? 0],
      ["order-ahead locations", data?.locations_order_ahead?.length ?? 0],
      ["events (POS)", data?.events_payments?.length ?? 0],
      ["events (ticketing)", data?.events_ticketing?.length ?? 0],
      ["events (invoicing)", data?.events_invoicing?.length ?? 0],
    ];
    const { data: web } = await client.query({
      query: WEB_CHECKOUT_USAGE,
      variables: {
        idStr: { payment_processor_id: String(processorId) },
        idNum: { payment_processor_id: processorId },
      },
      fetchPolicy: "network-only",
    });
    counts.push([
      "events (web checkout)",
      (web?.str?.aggregate?.count ?? 0) + (web?.num?.aggregate?.count ?? 0),
    ]);
    const used = counts.filter(([, n]) => n > 0);
    return {
      inUse: used.length > 0,
      summary: used.map(([label, n]) => `${n} ${label}`).join(", "),
    };
  } catch {
    return { inUse: true, summary: "usage check failed — not deleting" };
  }
}

/** Delete one orphan after a live usage re-check. */
async function deleteOrphanRow(
  p: StripeProcessorRow
): Promise<"deleted" | "in-use" | "error"> {
  // Belt and suspenders: never delete anything that isn't a true orphan,
  // regardless of what the caller passed.
  if (!isOrphanRow(p)) return "in-use";
  const usage = await processorUsageSummary(p.id);
  if (usage.inUse) return "in-use";
  try {
    await client.mutate({
      mutation: DeletePaymentProcessor,
      variables: { id: p.id },
    });
    return "deleted";
  } catch {
    return "error";
  }
}

/**
 * Full removal, strictly in this order:
 *   1. USAGE CHECK (fail closed) — refuse to touch anything while the
 *      processor is assigned to any event/location/web-checkout. The backend
 *      independently enforces the same gate before deauthorizing, so a stale
 *      UI can't sever an in-use processor either.
 *   2. Deauthorize at Stripe and VERIFY the platform's access is revoked.
 *   3. Re-check usage (race guard), then delete the row.
 * Nothing is severed or deleted unless every gate passes.
 */
async function performFullRemove(p: StripeProcessorRow, refresh: () => void) {
  const acct =
    p.config?.connected_account_id ||
    p.config?.previous_connected_account_id ||
    "(unknown)";
  try {
    const result = await deauthorizeAccount(p.id);
    if (!result.verified) {
      toast.error(
        `NOT removed — Stripe still reports platform access to ${acct}. ${
          result.detail ? `(${result.detail}) ` : ""
        }Platform-created Express accounts can't be severed this way.`
      );
      return;
    }
    // Gate 3: re-check right before the delete in case someone assigned it
    // between the first check and now. A severed row can't charge anything,
    // so keeping it briefly is harmless; deleting an assigned row is not.
    const usage = await processorUsageSummary(p.id);
    if (usage.inUse) {
      toast.error(
        `Stripe link severed (verified), but the row was kept — it was just assigned to: ${usage.summary}. Unassign it, then use Delete.`
      );
      refresh();
      return;
    }
    await client.mutate({
      mutation: DeletePaymentProcessor,
      variables: { id: p.id },
    });
    toast.success(`Severed at Stripe (verified) and removed`);
    refresh();
  } catch (err: any) {
    toast.error(`Full remove failed: ${err?.message || "unknown error"}`);
  }
}

async function performDeleteOrphan(p: StripeProcessorRow, refresh: () => void) {
  const result = await deleteOrphanRow(p);
  if (result === "deleted") {
    toast.success("Orphaned row deleted");
    refresh();
  } else if (result === "in-use") {
    toast.error(
      "Not deleted — this row is assigned to an event or location (or the usage check failed). Unassign it first."
    );
  } else {
    toast.error("Delete failed — try again");
  }
}

async function performBulkCleanup(
  orphans: StripeProcessorRow[],
  refresh: () => void
) {
  let deleted = 0;
  let skipped = 0;
  let failed = 0;
  for (const p of orphans) {
    const result = await deleteOrphanRow(p);
    if (result === "deleted") deleted += 1;
    else if (result === "in-use") skipped += 1;
    else failed += 1;
  }
  const parts = [`${deleted} deleted`];
  if (skipped) parts.push(`${skipped} skipped (in use / check failed)`);
  if (failed) parts.push(`${failed} failed`);
  (failed || skipped ? toast.error : toast.success)(
    `Orphan cleanup: ${parts.join(", ")}`
  );
  refresh();
}

// --- row context (passed to ag-grid cell renderers) ---------------------

interface RowContext {
  admin: boolean;
  refresh: () => void;
  onOpenLocation: (p: StripeProcessorRow) => void;
  onOpenDetails: (p: StripeProcessorRow) => void;
  // Open the in-app confirmation dialog for the row's destructive actions.
  requestRelink: (p: StripeProcessorRow) => void;
  requestUnlink: (p: StripeProcessorRow) => void;
  requestFullRemove: (p: StripeProcessorRow) => void;
  requestDeleteOrphan: (p: StripeProcessorRow) => void;
}

// --- row actions --------------------------------------------------------

type RowActionVariant = "primary" | "secondary" | "ghost" | "warning";

interface RowAction {
  label: string;
  onClick: () => void;
  variant: RowActionVariant;
  title?: string;
}

const ACTION_VARIANT: Record<RowActionVariant, string> = {
  primary: "bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500",
  secondary:
    "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-gray-400",
  ghost:
    "border border-primary-200 bg-white text-primary-700 hover:bg-primary-50 focus:ring-primary-500",
  warning:
    "border border-yellow-400 bg-yellow-50 text-yellow-800 hover:bg-yellow-100 focus:ring-yellow-500",
};

/**
 * The two non-destructive actions a row offers, as data rather than markup.
 *
 * The desktop grid and the mobile card list both render these, and they have
 * to agree about WHICH action a row gets — a row showing "Continue" in the
 * grid and "Dashboard" on a phone would be a genuine lie about whether that
 * merchant still has onboarding left to do. Returning descriptors keeps one
 * source of truth for that decision while letting each surface size its own
 * buttons (compact in a 44px grid row, full-width and thumb-sized on a phone).
 *
 * Destructive actions (Unlink / Remove / Delete) are deliberately NOT here.
 * They are admin-only and desktop-only, so they stay inline in ActionsCell.
 */
function buildRowActions(
  p: StripeProcessorRow,
  ctx: RowContext
): { primary: RowAction | null; location: RowAction | null } {
  const connected = hasConnectedAccount(p);
  const onboarded = isFullyOnboarded(p);
  const previous = hasPreviousAccount(p);
  const hasLocation = hasTerminalLocation(p);

  let primary: RowAction | null;
  if (!connected && previous && ctx.admin) {
    primary = {
      label: "Relink",
      variant: "ghost",
      onClick: () => ctx.requestRelink(p),
      title: `Re-attach the previous Stripe account (${p.config?.previous_connected_account_id})`,
    };
  } else if (!connected) {
    primary = {
      label: previous ? "Connect new" : "Connect",
      variant: "primary",
      onClick: () => handleConnect(p),
    };
  } else if (!onboarded) {
    primary = {
      label: "Continue",
      variant: "primary",
      onClick: () => handleConnect(p),
    };
  } else {
    primary = {
      label: "Dashboard",
      variant: "secondary",
      onClick: () => handleOpenDashboard(p),
      title:
        "Open the Stripe dashboard for this connected account (Express dashboard for legacy Express accounts, dashboard.stripe.com for the rest)",
    };
  }

  const location: RowAction | null = connected
    ? {
        label: hasLocation ? "Edit location" : "Add location",
        variant: hasLocation ? "secondary" : "warning",
        onClick: () => ctx.onOpenLocation(p),
        title: hasLocation
          ? "Edit the Terminal location for this account"
          : "Required before connecting a card reader",
      }
    : null;

  return { primary, location };
}

function ActionButton({
  action,
  size = "sm",
}: {
  action: RowAction;
  size?: "sm" | "lg";
}) {
  const sizing =
    size === "lg" ? "h-10 px-4 text-sm w-full" : "h-7 px-3 text-xs";
  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center font-medium rounded-md whitespace-nowrap transition-colors focus:outline-none focus:ring-1 focus:ring-offset-1 ${sizing} ${ACTION_VARIANT[action.variant]}`}
      onClick={action.onClick}
      title={action.title}
    >
      {action.label}
    </button>
  );
}

// --- action handlers ----------------------------------------------------

async function handleConnect(p: StripeProcessorRow) {
  const hasAccount = !!p.config?.connected_account_id;
  const isOAuthAccount = hasAccount && p.config?.stripe_link_type === "oauth";

  try {
    if (!isOAuthAccount) {
      // No account yet, or a platform-created account (legacy Express or
      // controller-based) that hasn't finished Stripe-hosted onboarding.
      // createOnboardingLink creates a platform-CONTROLLED account when the
      // row has none (OAuth is retired: accounts Ronin doesn't control can
      // never run the POS app on our readers — they boot to the Stripe
      // splash screen), and otherwise returns an Account Link resuming
      // wherever the merchant left off.
      const returnUrl = `${window.location.origin}/stripe-connect/list?onboarding=complete&processorId=${p.id}`;
      const refreshUrl = `${window.location.origin}/stripe-connect/list?onboarding=refresh&processorId=${p.id}`;
      const { url } = await createOnboardingLink(p.id, returnUrl, refreshUrl);
      window.location.href = url;
      return;
    }

    // Legacy OAuth Standard account that isn't fully onboarded — Stripe's
    // side of the verification isn't complete. There's nothing for us to
    // "resume" on our hosted onboarding (we never owned it). Send the
    // merchant to dashboard.stripe.com to finish in their own account.
    const { url } = await createLoginLink(p.id);
    window.open(url, "_blank", "noopener,noreferrer");
  } catch (err: any) {
    toast.error(`Stripe Connect: ${err.message || "failed to start"}`);
  }
}

async function handleOpenDashboard(p: StripeProcessorRow) {
  try {
    const { url } = await createLoginLink(p.id);
    window.open(url, "_blank", "noopener,noreferrer");
  } catch (err: any) {
    toast.error(`Stripe Dashboard: ${err.message || "failed to open"}`);
  }
}

async function performRelink(p: StripeProcessorRow, refresh: () => void) {
  try {
    await relinkPreviousAccount(p.id);
    toast.success("Stripe account relinked");
    refresh();
  } catch (err: any) {
    toast.error(`Relink failed: ${err.message || "unknown error"}`);
  }
}

async function performDisconnect(p: StripeProcessorRow, refresh: () => void) {
  try {
    await disconnectAccount(p.id);
    toast.success("Stripe account unlinked");
    refresh();
  } catch (err: any) {
    toast.error(`Unlink failed: ${err.message || "unknown error"}`);
  }
}

// --- ag-grid cell renderers ---------------------------------------------

const NameCell = (
  params: ICellRendererParams<StripeProcessorRow> & { context: RowContext }
) => {
  const p = params.data;
  if (!p) return null;
  return (
    <button
      type="button"
      onClick={() => params.context.onOpenDetails(p)}
      className="text-left text-sm text-primary-700 hover:text-primary-900 hover:underline truncate"
      title="Click to see full status breakdown"
    >
      {p.payment_processor}
    </button>
  );
};

// Is this venue's Terminal location on the account it belongs on? Both halves
// are persisted by the status refresh: `expected` from the same rule that
// decides placement at setup, `actual` from probing Stripe. A venue is only
// "misplaced" when we are sure of both AND they disagree.
export const locationIsMisplaced = (p: StripeProcessorRow): boolean => {
  const expected = p.config?.terminal_location_expected;
  const actual = p.config?.terminal_location_actual;
  if (!expected || expected === "unknown") return false;
  if (!actual || actual === "none" || actual === "unknown") return false;
  return expected !== actual;
};

// Plain-text placement verdict — the single source of truth for both the grid
// cell and the CSV export, so the column on screen and the column in the file
// can never drift apart.
export const placementLabel = (p: StripeProcessorRow): string => {
  const expected = p.config?.terminal_location_expected as string | undefined;
  const actual = p.config?.terminal_location_actual as string | undefined;

  if (!expected && !actual) return "Not checked";
  if (actual === "missing") return "Location missing";
  if (locationIsMisplaced(p)) {
    return expected === "platform" ? "Move to Ronin" : "Move to merchant";
  }
  return actual === "platform"
    ? "Ronin account"
    : actual === "connected"
    ? "Merchant account"
    : actual === "none"
    ? "No location"
    : "Unknown";
};

// Which account the Terminal location ACTUALLY sits on, normalised to one word.
// placementLabel() answers "is this right?", which is the question on screen;
// this answers "whose account is it on?", which is the question you want when
// counting or pivoting a CSV. They differ for a misplaced row: a row labelled
// "Move to Ronin" is still sitting on the Merchant account today.
export const placementAccount = (p: StripeProcessorRow): string => {
  const actual = p.config?.terminal_location_actual as string | undefined;
  return actual === "platform"
    ? "Ronin"
    : actual === "connected"
    ? "Merchant"
    : actual === "none"
    ? "None"
    : actual === "missing"
    ? "Missing"
    : "Unknown";
};

const PlacementCell = (params: ICellRendererParams<StripeProcessorRow>) => {
  const p = params.data;
  if (!p) return null;
  const expected = p.config?.terminal_location_expected as string | undefined;
  const actual = p.config?.terminal_location_actual as string | undefined;
  const reason = p.config?.terminal_location_expected_reason as string | undefined;

  if (!expected && !actual) {
    return (
      <span className="text-xs text-gray-400" title="Run Sync statuses to check">
        Not checked
      </span>
    );
  }
  if (actual === "missing") {
    return (
      <span
        className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200"
        title="The location this row names does not exist on either account. No reader can be registered against it."
      >
        Location missing
      </span>
    );
  }
  if (locationIsMisplaced(p)) {
    return (
      <span
        className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200"
        title={
          expected === "platform"
            ? `Should be on Ronin's platform account${reason ? ` — ${reason}` : ""}`
            : `Should be on the merchant's own account${reason ? ` — ${reason}` : ""}`
        }
      >
        {expected === "platform" ? "Move to Ronin" : "Move to merchant"}
      </span>
    );
  }
  const label = placementLabel(p);
  return (
    <span className="text-xs text-gray-600" title={reason || undefined}>
      {label}
    </span>
  );
};

// A row that exists for testing rather than for taking real money. Two
// signals, because they arrive by different routes: the row can be explicitly
// flagged test-mode, or it can belong to the Ronin Developers org, which
// getStripeClientForProcessor routes to the Stripe test key automatically.
// Name-based guesses are deliberately NOT used — "QA S700" is a real string in
// real merchant names and matching on it would hide live venues.
export const isTestRow = (p: StripeProcessorRow): boolean => {
  if (p.config?.stripe_mode === "test") return true;
  const org = (p.organization?.name || "").trim().toLowerCase();
  return org === "ronin developers";
};

const StatusCell = (params: ICellRendererParams<StripeProcessorRow>) => {
  const p = params.data;
  if (!p) return null;
  // Flag ONLY accounts Stripe has actually confirmed can't receive the Ronin
  // POS app (stripe_is_controller=false, persisted by Refresh from Stripe).
  // Deliberately NOT keyed off stripe_link_type: field testing showed some
  // OAuth-flow accounts (merchants who signed up fresh through our OAuth
  // link) ARE single-platform-controlled, addable to deploy groups, and run
  // the app fine — only Stripe's own controller check tells them apart from
  // pre-existing/multi-platform accounts like the ones that fail.
  const devicesBlocked =
    hasConnectedAccount(p) && p.config?.stripe_is_controller === false;
  return (
    <div className="flex items-center gap-2 h-full whitespace-nowrap overflow-hidden">
      <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${statusDotColor(p)}`} />
      <span className="text-sm truncate">{statusLabel(p)}</span>
      {devicesBlocked && (
        <span
          className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 shrink-0"
          title="Stripe has confirmed this account can't receive the Ronin POS app (not controlled by a single platform), and its locations can't join our deploy groups. Devices moved here boot to the Stripe splash screen. Create a replacement via 'New Stripe Connect Account' and re-onboard the merchant."
        >
          Devices blocked
        </span>
      )}
    </div>
  );
};

const TerminalLocationCell = (
  params: ICellRendererParams<StripeProcessorRow> & { context: RowContext }
) => {
  const p = params.data;
  if (!p) return null;
  const loc = p.config?.platform_account_location_id;
  const connected = hasConnectedAccount(p);

  if (loc) {
    return (
      <div className="flex items-center gap-2 h-full text-xs font-mono text-gray-700 whitespace-nowrap overflow-hidden">
        <span className="inline-block w-2 h-2 rounded-full bg-green-500 shrink-0" />
        <span className="truncate" title={loc}>{loc}</span>
      </div>
    );
  }
  if (!connected) {
    return <span className="text-xs text-gray-400">—</span>;
  }
  return (
    <div className="flex items-center gap-2 h-full whitespace-nowrap overflow-hidden">
      <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600 shrink-0" />
      <span className="text-xs text-yellow-700 truncate">Missing — add one</span>
    </div>
  );
};

/**
 * One connected account, as a card, for viewports below `md`.
 *
 * The grid is genuinely unusable on a phone: six columns plus up to four
 * buttons per row means the actions that matter — Continue, Add location —
 * sit off the right edge behind horizontal scroll, which is exactly what a
 * vendor opening this page on their phone is trying to reach.
 *
 * Deliberately NOT a reflow of the grid row. It shows the four things a
 * vendor needs (who, what state, which account, whether readers will work)
 * and omits Organization, Created and Location placement, which are
 * cross-account ops columns. Destructive actions are omitted entirely — they
 * are admin-only and a full-width Remove button under a thumb is a bad idea.
 */
function MobileAccountCard({
  p,
  ctx,
}: {
  p: StripeProcessorRow;
  ctx: RowContext;
}) {
  const { primary, location } = buildRowActions(p, ctx);
  const connected = hasConnectedAccount(p);
  const locationSet = hasTerminalLocation(p);
  const devicesBlocked =
    connected && p.config?.stripe_is_controller === false;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-gray-900 break-words">
            {p.payment_processor}
          </div>
          {ctx.admin && p.organization?.name && (
            <div className="text-xs text-gray-500 mt-0.5">
              {p.organization.name}
            </div>
          )}
        </div>
        <span
          className={`mt-1.5 inline-block w-2.5 h-2.5 rounded-full shrink-0 ${statusDotColor(p)}`}
        />
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="text-sm text-gray-700">{statusLabel(p)}</span>
        {devicesBlocked && (
          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
            Devices blocked
          </span>
        )}
      </div>

      <dl className="mt-3 space-y-2 text-xs">
        <div>
          <dt className="text-gray-500">Connected account</dt>
          <dd className="font-mono break-all text-gray-800">
            {p.config?.connected_account_id || "—"}
          </dd>
        </div>
        {connected && (
          <div>
            <dt className="text-gray-500">Terminal location</dt>
            <dd
              className={
                locationSet
                  ? "font-mono break-all text-gray-800"
                  : "text-yellow-800"
              }
            >
              {locationSet
                ? p.config?.platform_account_location_id
                : "Not set up — card readers will not work here"}
            </dd>
          </div>
        )}
      </dl>

      {(primary || location) && (
        <div className="mt-4 flex flex-col gap-2">
          {primary && <ActionButton action={primary} size="lg" />}
          {location && <ActionButton action={location} size="lg" />}
        </div>
      )}
    </div>
  );
}

/**
 * A vendor sees a handful of rows; an admin on a phone would otherwise render
 * every one of ~280 cards. Cap it and say so rather than silently truncating.
 */
const MOBILE_CARD_LIMIT = 50;

const ActionsCell = (
  params: ICellRendererParams<StripeProcessorRow> & { context: RowContext }
) => {
  const p = params.data;
  if (!p) return null;
  const {
    admin,
    refresh,
    requestUnlink,
    requestFullRemove,
    requestDeleteOrphan,
  } = params.context;
  const connected = hasConnectedAccount(p);

  // Only the destructive slot styles itself here now; slots 1 and 2 are
  // rendered by <ActionButton> from the shared descriptors.
  const btnDanger =
    "inline-flex items-center justify-center h-7 px-3 text-xs font-medium rounded-md whitespace-nowrap transition-colors focus:outline-none focus:ring-1 focus:ring-offset-1 text-red-600 hover:bg-red-50 focus:ring-red-500";

  // Pick which button (if any) goes in each fixed slot.
  // Slot 1 — primary forward action: Connect / Continue / Dashboard / Relink (admin)
  // Slot 2 — Terminal location: Edit / Add (only when connected)
  // Slot 3 — Unlink / Remove / Delete (admin only, desktop only)
  // Slots 1 and 2 come from buildRowActions so this grid and the mobile card
  // list can never disagree about which action a row offers.
  const { primary: primaryAction, location: locationAction } = buildRowActions(
    p,
    params.context
  );
  const slot1: JSX.Element | null = primaryAction ? (
    <ActionButton action={primaryAction} />
  ) : null;
  const slot2: JSX.Element | null = locationAction ? (
    <ActionButton action={locationAction} />
  ) : null;

  // Unlink is admin-only. Dashboard organizers never see this slot
  // populated, and the backend also enforces admin-only on the endpoint.
  // Orphan rows (no account, no relink stash) get a Delete instead — same
  // slot, admin-only, with a live usage re-check before anything is removed.
  const slot3: JSX.Element | null = connected && admin ? (
    <>
      <button
        type="button"
        className={btnDanger}
        onClick={() => requestUnlink(p)}
        title="Soft unlink: clears the link in our system only. Stripe authorization stays in place and the account can be relinked in one click."
      >
        Unlink
      </button>
      <button
        type="button"
        className={btnDanger}
        onClick={() => requestFullRemove(p)}
        title="Hard removal: revokes Ronin's access at Stripe, verifies the link is severed, then deletes this row so it can't be assigned."
      >
        Remove
      </button>
    </>
  ) : !connected && admin && hasPreviousAccount(p) ? (
    <button
      type="button"
      className={btnDanger}
      onClick={() => requestFullRemove(p)}
      title="This row was soft-unlinked but the Stripe authorization still exists. Revokes it at Stripe (verified), then deletes the row."
    >
      Remove
    </button>
  ) : !connected && admin && isOrphanRow(p) ? (
    <button
      type="button"
      className={btnDanger}
      onClick={() => requestDeleteOrphan(p)}
      title="Abandoned onboarding — no Stripe account attached. Deletes only our record."
    >
      Delete
    </button>
  ) : null;

  // Fixed 3-column grid: each slot keeps its column even when empty,
  // so Continue / Dashboard / Connect always sit in the same x-position
  // across every row.
  return (
    <div
      className="grid items-center h-full whitespace-nowrap"
      style={{ gridTemplateColumns: "110px 130px 150px", columnGap: "8px" }}
    >
      <div className="flex items-center justify-center">{slot1}</div>
      <div className="flex items-center justify-center">{slot2}</div>
      <div className="flex items-center justify-center gap-1">{slot3}</div>
    </div>
  );
};

// --- page ----------------------------------------------------------------

export const StripeConnectList = () => {
  const processors = useLoaderData() as StripeProcessorRow[];
  const revalidator = useRevalidator();
  const [searchParams, setSearchParams] = useSearchParams();
  const admin = isAdminUser();
  // Seeded once from ?search= so shared links keep working, then kept local —
  // typing shouldn't push a route change and re-run the loader.
  const [search, setSearch] = useState<string>(
    () => searchParams.get("search") || ""
  );

  // Top-bar filters (Layout). These are the org/event dropdowns in the header.
  // Stripe Connect rows don't have a direct event link, but events belong to
  // orgs — so an event selection narrows to its parent org.
  const headerOrgId = useOrganizationStore((s) => s.organizationId);
  const headerEventId = useEventStore((s) => s.eventId);
  const allEvents = useEventStore((s) => s.allEvents);
  const organizations = useOrganizationStore((s) => s.organizations);

  // An event selection narrows this grid to the event's parent org, which
  // means resolving the event id against the event store. That resolution has
  // three outcomes and every one of them has to fail toward SHOWING DATA:
  // hiding rows from someone who should see them is the failure mode this page
  // has already been burned by.
  //
  //   resolved      -> filter by the event's org
  //   still loading -> wait (allEvents genuinely empty)
  //   unresolvable  -> DO NOT FILTER
  //
  // The last one matters most. An id that cannot be resolved against a
  // populated allEvents is a deleted event, an event from another org, or a
  // stale value left in localStorage — and treating that as "keep waiting"
  // empties the grid permanently, since nothing will ever resolve it. `pending`
  // is therefore bounded to the one case that actually clears on its own.
  const { orgId: effectiveOrgId, pending: orgFilterPending } = useMemo<{
    orgId: number | null;
    pending: boolean;
  }>(() => {
    // Org id 0 is the header picker's "All Organizations" sentinel — treat it
    // as no filter (matching the loader and PaymentProcessorList), otherwise
    // the admin all-orgs view filters to org 0 and renders an empty grid.
    if (headerOrgId != null && Number(headerOrgId) !== 0) {
      return { orgId: Number(headerOrgId), pending: false };
    }

    // "All Events" in the header picker is the EMPTY STRING, not null
    // (renderEventSidebarOptions in modules/reports/utils). `"" != null` is
    // true and Number("") is 0, so a naive null check treats the most ordinary
    // selection in the top bar as "some event with id 0" — which resolves
    // against nothing. Normalize to a real id or nothing at all.
    // The store DECLARES eventId as number|null but its setter is typed `any`
    // (store/event.ts), and the picker writes "" through it — so TypeScript
    // will insist this comparison is impossible while production disagrees.
    // Widen before checking rather than trusting the declared type.
    const rawEventId = headerEventId as unknown as
      | number
      | string
      | null
      | undefined;
    const parsed =
      rawEventId === null || rawEventId === undefined || rawEventId === ""
        ? null
        : Number(rawEventId);
    const eventId =
      parsed != null && Number.isFinite(parsed) && parsed > 0 ? parsed : null;

    if (eventId != null) {
      const events = allEvents || [];
      const evt = events.find((e: any) => Number(e?.id) === eventId);
      if (evt) {
        const evtOrg = evt?.organization_id;
        return {
          orgId: evtOrg != null ? Number(evtOrg) : null,
          pending: false,
        };
      }
      // Not found. Only wait if the store has nothing in it yet — that is the
      // one case that resolves itself. A populated list that simply does not
      // contain this id never will, so show everything rather than nothing.
      if (events.length === 0) return { orgId: null, pending: true };
      return { orgId: null, pending: false };
    }

    return { orgId: null, pending: false };
  }, [headerOrgId, headerEventId, allEvents]);

  const activeOrgName = useMemo<string | null>(() => {
    if (effectiveOrgId == null) return null;
    const org = (organizations || []).find(
      (o: any) => Number(o?.id) === effectiveOrgId
    );
    return org?.name ?? null;
  }, [effectiveOrgId, organizations]);

  const activeEventName = useMemo<string | null>(() => {
    if (headerEventId == null) return null;
    const evt = (allEvents || []).find(
      (e: any) => Number(e?.id) === Number(headerEventId)
    );
    return evt?.name ?? null;
  }, [headerEventId, allEvents]);

  // Clears the top-bar selection that is narrowing this grid. Both stores are
  // localStorage-persisted, so without this the only way out was logging out.
  // 0 is the header picker's "All Organizations" sentinel, not a real org.
  //
  // setOrganizationId already clears the event id and refetches events, but it
  // does NOT refresh the org-scoped collections (locations, clerks,
  // vendorLocations) — every other caller pairs it with initOrganization(), and
  // skipping that leaves a global, persisted store describing the OLD org on
  // every other page. Revalidate too: for a non-admin the loader baked the old
  // organization_id into its `where`, so without re-running it the button
  // changes the banner and nothing else.
  const clearHeaderFilter = () => {
    const orgState = useOrganizationStore.getState();
    orgState.setOrganizationId(0);
    orgState.initOrganization();
    useEventStore.getState().setEventId(null);
    revalidator.revalidate();
  };

  const [locationModalFor, setLocationModalFor] = useState<StripeProcessorRow | null>(null);
  const [detailsPanelFor, setDetailsPanelFor] = useState<StripeProcessorRow | null>(null);

  const onboardingState = searchParams.get("onboarding");
  const returnProcessorId = searchParams.get("processorId");
  // Stripe sends back ?code=ac_...&state=<processorId>:<mode> after OAuth.
  // We detect the OAuth return purely by the presence of these two params
  // because Stripe's redirect_uri matching is strict — registering a URI with
  // our own ?oauth=complete marker requires us to register every variation,
  // so we register the bare URL and use Stripe's own params as the signal.
  const oauthCode = searchParams.get("code");
  const oauthStateParam = searchParams.get("state"); // "<processorId>:<mode>" or "new:<...>"
  // Two shapes of state come back:
  //   "<processorId>:<mode>"  → resume/patch an existing row
  //   "new:<base64url>"       → create the row now (deferred-creation flow)
  const isResumeOAuthState =
    !!oauthStateParam && /^\d+:(test|live)$/.test(oauthStateParam);
  const isNewOAuthState =
    !!oauthStateParam && oauthStateParam.startsWith("new:");
  const isOAuthReturn =
    !!oauthCode && (isResumeOAuthState || isNewOAuthState);

  // After Stripe-hosted onboarding redirects back, refresh status and then
  // if the row has no Terminal Location, auto-open the modal so the dashboard
  // organizer gets immediately prompted for one.
  useEffect(() => {
    let cancelled = false;
    if (onboardingState === "complete" && returnProcessorId) {
      (async () => {
        try {
          await refreshAccountStatus(Number(returnProcessorId));
          if (cancelled) return;
          toast.success("Stripe account status refreshed");
        } catch (err: any) {
          if (cancelled) return;
          toast.error(`Status refresh failed: ${err.message || "unknown"}`);
        } finally {
          if (!cancelled) {
            setSearchParams({}, { replace: true });
            revalidator.revalidate();
            // Auto-prompt for Terminal Location if missing.
            const idNum = Number(returnProcessorId);
            const row = processors.find((p) => p.id === idNum);
            if (row && hasConnectedAccount(row) && !hasTerminalLocation(row)) {
              setLocationModalFor(row);
            }
          }
        }
      })();
    } else if (onboardingState === "refresh" && returnProcessorId) {
      (async () => {
        try {
          const returnUrl = `${window.location.origin}/stripe-connect/list?onboarding=complete&processorId=${returnProcessorId}`;
          const refreshUrl = `${window.location.origin}/stripe-connect/list?onboarding=refresh&processorId=${returnProcessorId}`;
          const { url } = await createOnboardingLink(
            Number(returnProcessorId),
            returnUrl,
            refreshUrl
          );
          if (!cancelled) window.location.href = url;
        } catch (err: any) {
          if (cancelled) return;
          toast.error(`Could not restart onboarding: ${err.message}`);
          setSearchParams({}, { replace: true });
        }
      })();
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingState, returnProcessorId]);

  // After returning from onboarding, Stripe often hasn't flipped charges/payouts
  // on yet, so the row lands on "Onboarding Incomplete". Nudge a couple of delayed
  // status refreshes so the row self-updates to Active without the merchant
  // clicking Continue. The account.updated webhook is the durable fix; this just
  // narrows the visible-stale window right after return.
  //
  // IMPORTANT: the timers are gated on UNMOUNT only (unmountedRef), not on the
  // OAuth effect's `cancelled` flag. The success path clears the ?code/&state
  // params, which re-runs that effect and flips its `cancelled` during normal
  // operation — gating on it silently cancelled every recheck before it fired.
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);
  const scheduleStatusRecheck = (processorId: number) => {
    [4000, 9000].forEach((delay) => {
      setTimeout(async () => {
        if (unmountedRef.current) return;
        try {
          await refreshAccountStatus(processorId);
          if (!unmountedRef.current) revalidator.revalidate();
        } catch {
          /* ignore — the webhook will heal it */
        }
      }, delay);
    });
  };

  // Filter: show only accounts Stripe has confirmed will block devices.
  const [blockedOnly, setBlockedOnly] = useState(false);
  // Test rows are noise in the all-organizations view — they outnumber nothing
  // useful and they sit alphabetically among live venues. Hidden by default
  // there, always shown when a specific org is selected (if you have picked
  // Ronin Developers, its rows are what you came for).
  const [showTestRows, setShowTestRows] = useState(false);

  // Bulk-sync statuses from Stripe once per page load, so the grid tells the
  // truth without ops clicking into every row (statuses previously only
  // updated when a row's panel was opened). 'stale' scope touches only rows
  // that aren't fully onboarded — plus rows missing the deployability flag —
  // so repeat loads cost almost nothing.
  const [syncBusy, setSyncBusy] = useState(false);
  const bulkRefreshFiredRef = useRef(false);
  useEffect(() => {
    if (bulkRefreshFiredRef.current) return;
    bulkRefreshFiredRef.current = true;
    (async () => {
      try {
        const r = await refreshAllAccountStatuses("stale");
        if (unmountedRef.current) return;
        if (r.changed > 0) {
          toast.success(
            `Statuses synced from Stripe — ${r.changed} account${r.changed === 1 ? "" : "s"} updated`
          );
          revalidator.revalidate();
        }
        if (r.errors?.length) {
          // eslint-disable-next-line no-console
          console.warn("Bulk status refresh errors:", r.errors);
        }
      } catch (err: any) {
        // Non-fatal: the page still renders from DB state; per-row refresh
        // and the manual sync button remain available.
        // eslint-disable-next-line no-console
        console.warn("Bulk status refresh failed:", err?.message || err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Export EXACTLY what's on screen — rowData already reflects the search
  // box, the org/event header filter, and the "Devices blocked only" toggle,
  // so a filtered view exports as a filtered CSV (same pattern as
  // DeviceList's handleExportCSV).
  const handleExportCSV = () => {
    const devicesVerdict = (p: StripeProcessorRow): string => {
      if (!hasConnectedAccount(p)) return "";
      if (p.config?.stripe_is_controller === false) return "Blocked";
      if (p.config?.stripe_is_controller === true) return "OK";
      return "Unverified";
    };
    const header = [
      "Name", "Organization", "Status", "Devices",
      "Connected Account", "Terminal Location",
      "Location Placement", "Location On", "Mode",
      "Vendor", "Onboarded At", "Row Created",
    ];
    const lines = [header.join(",")].concat(
      rowData.map((p) =>
        [
          p.payment_processor ?? "",
          p.organization?.name ?? "",
          statusLabel(p),
          devicesVerdict(p),
          p.config?.connected_account_id ?? "",
          p.config?.platform_account_location_id ?? "",
          placementLabel(p),
          placementAccount(p),
          p.config?.stripe_mode ?? "",
          p.config?.vendor_name ?? "",
          p.config?.stripe_onboarded_at ?? "",
          p.created_at ?? "",
        ]
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(",")
      )
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `StripeConnect${blockedOnly ? "_DevicesBlocked" : ""}_${moment().format("YYYYMMDD_HHmmss")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rowData.length} row${rowData.length === 1 ? "" : "s"}`);
  };

  const handleSyncAll = async () => {
    setSyncBusy(true);
    try {
      const r = await refreshAllAccountStatuses("all");
      toast.success(
        `Synced ${r.refreshed} accounts from Stripe — ${r.changed} updated` +
          (r.errors.length ? ` (${r.errors.length} failed)` : "")
      );
      revalidator.revalidate();
    } catch (err: any) {
      toast.error(`Status sync failed: ${err?.message || "unknown"}`);
    } finally {
      setSyncBusy(false);
    }
  };

  // Guards against submitting the same OAuth code twice from this mount
  // (re-renders / double-fired effects). Stripe codes are single-use; a second
  // POST either errors or — worst case, racing the first — used to create a
  // duplicate processor row and a duplicate Slack ping. The backend now also
  // dedups server-side; this just stops the double call at the source.
  const oauthCodeHandledRef = useRef<string | null>(null);

  // Handle return from Stripe OAuth (Standard) authorization. Stripe redirects
  // here with ?code=ac_xxx&state=<processorId>:<mode>. We hand the code to the
  // backend, which exchanges it for an access token and the connected acct_...
  // id, and stores both on the processor row.
  useEffect(() => {
    if (!isOAuthReturn || !oauthCode || !oauthStateParam) return;
    if (oauthCodeHandledRef.current === oauthCode) return;
    oauthCodeHandledRef.current = oauthCode;
    let cancelled = false;
    (async () => {
      try {
        if (isNewOAuthState) {
          // Deferred-creation flow: the backend creates the row now, after a
          // successful code exchange. The new row isn't in the current loader
          // data yet, so we just revalidate; the yellow banner + "Add location"
          // button guide the user to the Terminal-location step.
          const result = await completeOAuthLinkForNew(oauthCode, oauthStateParam);
          if (cancelled) return;
          toast.success("Stripe account connected");
          setSearchParams({}, { replace: true });
          revalidator.revalidate();
          if (result?.processorId && !result?.isFullyOnboarded) {
            scheduleStatusRecheck(result.processorId);
          }
          return;
        }

        const processorIdFromState = Number(oauthStateParam.split(":")[0]);
        if (!processorIdFromState || Number.isNaN(processorIdFromState)) {
          toast.error("Stripe OAuth: missing processor id in state");
          setSearchParams({}, { replace: true });
          return;
        }
        const result = await completeOAuthLink(processorIdFromState, oauthCode, oauthStateParam);
        if (cancelled) return;
        toast.success("Stripe account linked");
        // Auto-prompt for a Terminal Location if the freshly-linked account
        // doesn't have one yet (mirrors the Express post-onboarding flow).
        setSearchParams({}, { replace: true });
        revalidator.revalidate();
        const row = processors.find((p) => p.id === processorIdFromState);
        if (row && !row.config?.platform_account_location_id) {
          setLocationModalFor(row);
        }
        if (!result?.isFullyOnboarded) {
          scheduleStatusRecheck(processorIdFromState);
        }
      } catch (err: any) {
        if (cancelled) return;
        toast.error(`Stripe OAuth: ${err?.message || "failed to link"}`);
        setSearchParams({}, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOAuthReturn, oauthCode, oauthStateParam]);

  const rowData = useMemo(() => {
    // Filter not resolved yet — show nothing rather than a full unfiltered
    // list that is about to shrink. The banner explains the wait.
    if (orgFilterPending) return [];

    let rows = [...processors];
    if (effectiveOrgId != null) {
      rows = rows.filter(
        (p) => Number(p.organization_id) === effectiveOrgId
      );
    }
    // Matches name, organization, connected account id, previous account id
    // and terminal location id — see ../payment_processor_config/search.ts.
    rows = rows.filter((p) => matchesProcessorSearch(p, search));
    // "Devices blocked" filter: only accounts Stripe has confirmed can't
    // receive the Ronin POS app (see StatusCell). These are the merchants
    // that need a new Ronin-created account before their readers work.
    if (blockedOnly) {
      rows = rows.filter(
        (p) =>
          hasConnectedAccount(p) && p.config?.stripe_is_controller === false
      );
    }
    // Hide test rows ONLY in the cross-organization view. With a specific org
    // selected, show everything that org has — otherwise picking Ronin
    // Developers would show an empty page.
    if (effectiveOrgId == null && !showTestRows) {
      rows = rows.filter((p) => !isTestRow(p));
    }
    return rows.sort((a, b) =>
      a.payment_processor.localeCompare(b.payment_processor)
    );
  }, [processors, effectiveOrgId, orgFilterPending, search, blockedOnly, showTestRows]);

  const colDefs: ColDef<StripeProcessorRow>[] = useMemo(() => {
    const baseCellClass = "flex items-center";
    const cols: ColDef<StripeProcessorRow>[] = [
      {
        headerName: "Name",
        valueGetter: (p) => p.data?.payment_processor,
        cellRenderer: NameCell,
        filter: "agTextColumnFilter",
        cellClass: baseCellClass,
        pinned: "left",
        minWidth: 180,
      },
    ];
    if (admin) {
      cols.push({
        headerName: "Organization",
        valueGetter: (p) => p.data?.organization?.name || "",
        filter: "agTextColumnFilter",
        cellClass: `${baseCellClass} truncate`,
        minWidth: 160,
      });
    }
    cols.push(
      {
        headerName: "Status",
        valueGetter: (p) => (p.data ? statusLabel(p.data) : ""),
        cellRenderer: StatusCell,
        filter: "agTextColumnFilter",
        minWidth: 200,
      },
      {
        headerName: "Connected Account",
        valueGetter: (p) => p.data?.config?.connected_account_id || "",
        cellClass: `${baseCellClass} font-mono truncate`,
        filter: "agTextColumnFilter",
        minWidth: 220,
      },
      {
        headerName: "Terminal Location",
        valueGetter: (p) => p.data?.config?.platform_account_location_id || "",
        cellRenderer: TerminalLocationCell,
        filter: "agTextColumnFilter",
        minWidth: 200,
      },
      {
        headerName: "Location placement",
        valueGetter: (p) =>
          p.data && locationIsMisplaced(p.data)
            ? "Misplaced"
            : (p.data?.config?.terminal_location_actual as string) || "unchecked",
        cellRenderer: PlacementCell,
        filter: "agTextColumnFilter",
        cellClass: baseCellClass,
        minWidth: 160,
      },
      {
        headerName: "Created",
        valueGetter: (p) => p.data?.created_at,
        valueFormatter: (p) => (p.value ? moment(p.value).format("MM/DD/YYYY") : ""),
        cellClass: baseCellClass,
        filter: "agTextColumnFilter",
        minWidth: 110,
      },
      {
        headerName: "Actions",
        cellRenderer: ActionsCell,
        sortable: false,
        filter: false,
        minWidth: 440,
        pinned: "right",
      }
    );
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin]);

  // --- in-app confirmation dialog (replaces window.confirm) ---------------
  const [confirmReq, setConfirmReq] = useState<{
    title: string;
    message: ReactNode;
    confirmLabel: string;
    onConfirm: () => Promise<void>;
  } | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const runConfirm = async () => {
    if (!confirmReq || confirmBusy) return;
    setConfirmBusy(true);
    try {
      await confirmReq.onConfirm();
    } finally {
      setConfirmBusy(false);
      setConfirmReq(null);
    }
  };

  const rowContext: RowContext = useMemo(
    () => ({
      admin,
      refresh: () => revalidator.revalidate(),
      onOpenLocation: (p) => setLocationModalFor(p),
      onOpenDetails: (p) => setDetailsPanelFor(p),
      requestRelink: (p) => {
        const prev = p.config?.previous_connected_account_id;
        if (!prev) return;
        setConfirmReq({
          title: "Relink previous Stripe account?",
          message: (
            <p>
              Re-attach <span className="font-mono">{prev}</span> to{" "}
              <strong>{p.payment_processor}</strong>. This reconnects the
              existing Stripe account — no new account, no onboarding.
            </p>
          ),
          confirmLabel: "Relink",
          onConfirm: () => performRelink(p, () => revalidator.revalidate()),
        });
      },
      requestUnlink: (p) => {
        setConfirmReq({
          title: "Unlink Stripe account?",
          message: (
            <div className="space-y-2">
              <p>
                Removes the link between <strong>{p.payment_processor}</strong>{" "}
                and its Stripe account <em>in our system only</em>. The Stripe
                account itself stays active and authorized.
              </p>
              <p className="text-gray-500">
                The account id is stashed, so you can one-click Relink later.
                For a permanent removal use <strong>Remove</strong> instead.
              </p>
            </div>
          ),
          confirmLabel: "Unlink",
          onConfirm: () => performDisconnect(p, () => revalidator.revalidate()),
        });
      },
      requestFullRemove: async (p) => {
        const acct =
          p.config?.connected_account_id ||
          p.config?.previous_connected_account_id ||
          "(unknown)";
        // Gate 1: never offer to sever an assigned processor. (The backend
        // independently enforces the same gate before deauthorizing.)
        const preUsage = await processorUsageSummary(p.id);
        if (preUsage.inUse) {
          toast.error(
            `Cannot remove "${p.payment_processor}" — it's still assigned to: ${preUsage.summary}. Unassign it everywhere first (the processor's edit page lists every assignment).`
          );
          return;
        }
        setConfirmReq({
          title: `Fully remove ${p.payment_processor}?`,
          message: (
            <div className="space-y-2">
              <p className="text-gray-500">
                Checked: this processor is not assigned to any event or
                location.
              </p>
              <p>This will:</p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>
                  Revoke Ronin's access to{" "}
                  <span className="font-mono">{acct}</span> at Stripe — the
                  merchant keeps their Stripe account, login, and money; we
                  just lose the ability to charge it.
                </li>
                <li>Verify with Stripe that the link is actually severed.</li>
                <li>
                  Delete this processor row so it can't be assigned anywhere.
                </li>
              </ol>
              <p className="font-medium text-red-700">
                There is NO relink after this — reconnecting later means the
                merchant authorizes again from scratch.
              </p>
            </div>
          ),
          confirmLabel: "Sever & Remove",
          onConfirm: () => performFullRemove(p, () => revalidator.revalidate()),
        });
      },
      requestDeleteOrphan: (p) => {
        setConfirmReq({
          title: `Delete ${p.payment_processor}?`,
          message: (
            <p>
              This row never finished Stripe onboarding — there is no connected
              account attached. Deleting removes only our record; nothing
              exists on Stripe's side.
            </p>
          ),
          confirmLabel: "Delete",
          onConfirm: () =>
            performDeleteOrphan(p, () => revalidator.revalidate()),
        });
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [admin]
  );

  const missingLocationCount = useMemo(
    () => rowData.filter((p) => hasConnectedAccount(p) && !hasTerminalLocation(p)).length,
    [rowData]
  );
  // Rows where the merchant has a connected account but hasn't completed
  // Stripe-hosted onboarding. Either details haven't been submitted or
  // charges/payouts haven't been enabled by Stripe yet.
  const unfinishedOnboardingCount = useMemo(
    () => rowData.filter((p) => hasConnectedAccount(p) && !isFullyOnboarded(p)).length,
    [rowData]
  );
  // Abandoned onboarding rows in the CURRENT filtered view (respects the
  // org/event header filter, so "clean up" only touches what's on screen).
  const orphanRows = useMemo(() => rowData.filter(isOrphanRow), [rowData]);
  // Stripe-confirmed devices-blocked accounts across ALL rows (unfiltered),
  // so the toggle's count is stable regardless of search/org filters.
  // How many rows the test filter is hiding right now, so the toggle can say
  // so rather than leaving ops wondering where a row went.
  const hiddenTestCount = useMemo(
    () =>
      effectiveOrgId == null ? processors.filter((p) => isTestRow(p)).length : 0,
    [processors, effectiveOrgId]
  );

  const blockedCount = useMemo(
    () =>
      processors.filter(
        (p) =>
          hasConnectedAccount(p) && p.config?.stripe_is_controller === false
      ).length,
    [processors]
  );

  return (
    <div className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
        <div className="md:col-span-4">
          <Breadcrumbs />
          <h1 className="text-xl font-semibold mt-2">Stripe Connect</h1>
          <p className="text-sm text-gray-500">
            {admin
              ? "Admin view: all Stripe Connect accounts across all organizations."
              : "Your Stripe Connect accounts. Connect each one to Stripe-hosted onboarding to start accepting payments. Add a Terminal location for each connected account before plugging in a card reader."}
          </p>
          {orgFilterPending && (
            <div className="mt-2 inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
              <span>Loading the event filter…</span>
              {/* Deliberately available WHILE pending. If the event store never
                  arrives (failed fetch, hidden event picker) this is the only
                  way out of an empty grid short of logging out. */}
              <button
                type="button"
                onClick={clearHeaderFilter}
                className="underline underline-offset-2 hover:no-underline"
                title="Clear the organization and event selection in the top bar"
              >
                Show all accounts
              </button>
            </div>
          )}
          {!orgFilterPending && (activeOrgName || activeEventName) && (
            // Deliberately loud. This used to be 12px gray text that nobody
            // noticed, so a sticky event selection read as missing data — ops
            // saw 11 of 139 accounts and assumed the page was broken.
            <div className="mt-2 inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <span className="font-semibold">Filtered</span>
              <span>
                showing{" "}
                <span className="font-semibold">{rowData.length}</span> of{" "}
                <span className="font-semibold">{processors.length}</span>{" "}
                accounts for
              </span>
              {activeOrgName && <span className="font-semibold">{activeOrgName}</span>}
              {activeOrgName && activeEventName && <span>·</span>}
              {activeEventName && (
                <span className="font-semibold">{activeEventName}</span>
              )}
              <button
                type="button"
                onClick={clearHeaderFilter}
                className="ml-1 underline underline-offset-2 hover:no-underline"
                title="Clear the organization and event selection in the top bar"
              >
                Show all accounts
              </button>
            </div>
          )}
        </div>
        <div className="md:col-span-2 flex flex-col items-stretch md:items-end gap-2">
          <div className="w-full max-w-sm">
            <Input
              name="search"
              defaultValue={search}
              placeholder={PROCESSOR_SEARCH_PLACEHOLDER}
              onChange={(s) => setSearch(typeof s === "string" ? s : "")}
            />
            {search.trim().length > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                {rowData.length} {rowData.length === 1 ? "match" : "matches"}
              </p>
            )}
          </div>
          <div className="flex flex-wrap justify-end items-center gap-2">
          <button
            type="button"
            onClick={handleExportCSV}
            className="inline-flex items-center h-9 px-3 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 whitespace-nowrap"
            title="Download the rows currently shown (respects search, org filter, and the Devices blocked toggle) as a CSV."
          >
            Export CSV
          </button>
          <button
            type="button"
            disabled={syncBusy}
            onClick={handleSyncAll}
            className="inline-flex items-center h-9 px-3 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
            title="Re-pull every connected account's status and device deployability from Stripe. Stale rows are already synced automatically when this page loads."
          >
            {syncBusy ? "Syncing…" : "Sync statuses"}
          </button>
          {admin && orphanRows.length > 0 && (
            <button
              type="button"
              onClick={() =>
                setConfirmReq({
                  title: `Delete ${orphanRows.length} orphaned row${orphanRows.length === 1 ? "" : "s"}?`,
                  message: (
                    <div className="space-y-2">
                      <p>
                        These never finished Stripe onboarding — no connected
                        account, nothing to relink. Each row is re-checked for
                        event/location assignments right before deletion and
                        skipped if anything references it.
                      </p>
                      <ul className="list-disc pl-5 space-y-0.5 text-gray-600">
                        {orphanRows.slice(0, 12).map((p) => (
                          <li key={p.id}>{p.payment_processor}</li>
                        ))}
                      </ul>
                      {orphanRows.length > 12 && (
                        <p className="text-gray-500">
                          …and {orphanRows.length - 12} more
                        </p>
                      )}
                    </div>
                  ),
                  confirmLabel: `Delete ${orphanRows.length} row${orphanRows.length === 1 ? "" : "s"}`,
                  onConfirm: () =>
                    performBulkCleanup(orphanRows, () =>
                      revalidator.revalidate()
                    ),
                })
              }
              className="inline-flex items-center h-9 px-3 text-sm font-medium rounded-md border border-red-300 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50 whitespace-nowrap"
              title="Delete rows that never finished Stripe onboarding (no connected account, nothing to relink). Each row is re-checked for assignments before deletion."
            >
              {`Clean up ${orphanRows.length} orphaned row${orphanRows.length === 1 ? "" : "s"}`}
            </button>
          )}
          <NewStripeConnectAccountModal
            returnPath="/stripe-connect/list"
            label="New account"
          />
          </div>
          <label
            className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer whitespace-nowrap"
            title="Show only accounts Stripe has confirmed can't receive the Ronin POS app — the merchants that need a new Ronin-created account before their card readers work."
          >
            <input
              type="checkbox"
              className="rounded border-gray-300 text-red-600 focus:ring-red-500"
              checked={blockedOnly}
              onChange={(e) => setBlockedOnly(e.target.checked)}
            />
            Devices blocked only
            {blockedCount > 0 ? ` (${blockedCount})` : ""}
          </label>
          {/* Only meaningful across organizations — with one org selected its
              test rows are shown regardless. */}
          {effectiveOrgId == null && (
            <label
              className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer whitespace-nowrap"
              title="Test-mode rows, and anything under the Ronin Developers organization. Hidden by default across all organizations; always shown when you select a specific organization."
            >
              <input
                type="checkbox"
                className="rounded border-gray-300 text-gray-600 focus:ring-gray-500"
                checked={showTestRows}
                onChange={(e) => setShowTestRows(e.target.checked)}
              />
              Show test accounts
              {hiddenTestCount > 0 && !showTestRows
                ? ` (${hiddenTestCount} hidden)`
                : ""}
            </label>
          )}
        </div>
      </div>

      {missingLocationCount > 0 && (
        <div className="mt-3 flex items-start gap-2 border border-yellow-300 bg-yellow-50 text-yellow-800 rounded px-3 py-2 text-sm">
          <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong>
              {missingLocationCount} {missingLocationCount === 1 ? "account is" : "accounts are"} missing a Terminal location.
            </strong>{" "}
            Card readers will not work until you add one. Click <em>Add location</em> on the row to set it up.
          </div>
        </div>
      )}

      {unfinishedOnboardingCount > 0 && (
        <div className="mt-3 flex items-start gap-2 border border-yellow-300 bg-yellow-50 text-yellow-800 rounded px-3 py-2 text-sm">
          <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong>
              {unfinishedOnboardingCount} {unfinishedOnboardingCount === 1 ? "account hasn't" : "accounts haven't"} finished Stripe onboarding yet.
            </strong>{" "}
            Click <em>Continue</em> on the row to go to Stripe-hosted onboarding and fill out the business
            details, bank account, and identity verification. Make sure to click <em>Save and continue</em>
            at the bottom of each step — Stripe only persists progress when you do. Payments will not
            activate until Stripe confirms <em>charges</em> and <em>payouts</em> are enabled.
          </div>
        </div>
      )}

      {/* Below md: cards. See MobileAccountCard for why the grid is not just
          reflowed here. */}
      <div className="md:hidden mt-4 space-y-3">
        {rowData.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
            {orgFilterPending
              ? "Loading…"
              : "No Stripe Connect accounts match the current filters."}
          </div>
        ) : (
          <>
            {rowData.slice(0, MOBILE_CARD_LIMIT).map((row) => (
              <MobileAccountCard key={row.id} p={row} ctx={rowContext} />
            ))}
            {rowData.length > MOBILE_CARD_LIMIT && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-600">
                Showing the first {MOBILE_CARD_LIMIT} of {rowData.length}{" "}
                accounts. Search by name or account id to narrow this down.
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ height: "75vh" }} className="mt-4 hidden md:block">
        <div className="ag-theme-quartz" style={{ width: "100%", height: "100%" }}>
          <AgGridReact
            rowData={rowData}
            // Without getRowId the grid treats every new rowData array as a
            // wholesale replacement and throws away scroll position — which is
            // why saving a Terminal location (onSaved -> revalidate -> new
            // array) bounced you back to the top of a 139-row grid.
            getRowId={(p) => String(p.data.id)}
            columnDefs={colDefs}
            rowHeight={44}
            headerHeight={40}
            autoSizeStrategy={{ type: "fitGridWidth" as const }}
            pagination={true}
            paginationPageSize={100}
            gridOptions={{ enableCellTextSelection: true }}
            context={rowContext}
          />
        </div>
      </div>

      {locationModalFor && (
        <TerminalLocationModal
          processorId={locationModalFor.id}
          processorName={locationModalFor.payment_processor}
          existingLocationId={locationModalFor.config?.platform_account_location_id || null}
          onClose={() => setLocationModalFor(null)}
          onSaved={() => {
            setLocationModalFor(null);
            revalidator.revalidate();
          }}
        />
      )}

      {detailsPanelFor && (
        <StripeAccountDetailsPanel
          processor={detailsPanelFor}
          onClose={() => setDetailsPanelFor(null)}
          onRefreshed={() => revalidator.revalidate()}
        />
      )}

      <ConfirmDialog
        open={confirmReq !== null}
        title={confirmReq?.title ?? ""}
        message={confirmReq?.message ?? null}
        confirmLabel={confirmReq?.confirmLabel}
        busy={confirmBusy}
        onConfirm={runConfirm}
        onCancel={() => {
          if (!confirmBusy) setConfirmReq(null);
        }}
      />
    </div>
  );
};
